/**
 * Validates and parses a date string into a Date object
 * Handles:
 * - ISO 8601 strings (e.g., "2024-03-14T15:30:00Z")
 * - ISO-like strings (e.g., "2024-03-14 15:30:00")
 * - Unix timestamps (seconds or milliseconds)
 * - Common date formats (14.03.2024, 03/14/2024, etc.)
 */
export function parseDate(dateString: string): Date | null {
    if (!dateString) {
        return null;
    }

    // Try parsing as Unix timestamp (seconds or milliseconds)
    const timestamp = Number(dateString);
    if (!isNaN(timestamp)) {
        // If timestamp is in seconds (10 digits), convert to milliseconds
        const timestampMs = timestamp.toString().length <= 10 ? timestamp * 1000 : timestamp;
        const date = new Date(timestampMs);
        if (!isNaN(date.getTime())) {
            return date;
        }
    }

    // Try parsing as ISO 8601 or similar format
    let date = new Date(dateString);
    if (!isNaN(date.getTime())) {
        return date;
    }

    // Try parsing date with different separators
    const normalized = dateString.replace(
        /(\d{1,2})[-./](\d{1,2})[-./](\d{4})/,
        (_, d, m, y) => `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
    );

    date = new Date(normalized);
    if (!isNaN(date.getTime())) {
        return date;
    }

    return null;
}

/**
 * Formats a date using the provided locale
 * @returns Formatted time string or null if date is invalid
 */
export function formatDate(date: Date | string | null, locale: string): string | null {
    if (!date) {
        return null;
    }

    try {
        const dateObj = typeof date === "string" ? parseDate(date) : date;
        if (!dateObj || isNaN(dateObj.getTime())) {
            return null;
        }

        const formatter = new Intl.DateTimeFormat(locale, {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        });

        return formatter.format(dateObj);
    } catch {
        return null;
    }
}
